import { Breadcrumb, Button, Col, Container, Row } from "react-bootstrap";
import stringUtilities from "../../utilities/stringUtilities";
import Loading from "../common/loading";
import OrganisationSelector from "../common/organisationSelector";
import { useEffect, useRef, useState } from "react";
import useApi from "../../utilities/useApi";
import { useParams } from "react-router-dom";
import FolderModal from "./_folderModal";
import pointUtilities from "../../utilities/pointUtilities";

export default function Icons(props) {
    const { api } = useApi();
    const params = useParams();
    const [icons, setIcons] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [organisationId, setOrganisationId] = useState(stringUtilities.isNullOrEmpty(params.organisationId) ? props.organisationId : params.organisationId);

    const [currentPath, setCurrentPath] = useState("");

    const [showFolderModal, setShowFolderModal] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const pendingFoldersKey = (orgId) => `iconPendingFolders-${orgId}`;

    const [pendingFolders, setPendingFolders] = useState(() => {
        if (!organisationId) return [];
        try {
            const stored = sessionStorage.getItem(pendingFoldersKey(organisationId));
            return stored ? JSON.parse(stored) : [];
        } catch {
            return [];
        }
    });

    const sanitizeFolderName = (name) => name
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9\- \._]/g, '')
        .replace(/-+/g, '-')
        .replace(/^[-.]+|[-.]+$/g, '')
        .toLowerCase();

    const annotateFolders = (folders, prefix) => {
        if (!folders) return [];
        return folders.map(f => {
            const myPath = prefix ? `${prefix}/${f.name}` : f.name;
            return {
                ...f,
                path: myPath,
                folders: annotateFolders(f.folders ?? [], myPath)
            };
        });
    };

    const annotatePaths = (orgNode) => {
        if (!orgNode) return null;
        return {
            ...orgNode,
            path: "",
            folders: annotateFolders(orgNode.folders ?? [], "")
        };
    };

    const countImagesRecursive = (folder) => {
        if (!folder) return 0;
        const own = folder.images?.length ?? 0;
        const fromSubs = (folder.folders ?? []).reduce((sum, sub) => sum + countImagesRecursive(sub), 0);
        return own + fromSubs;
    };

    const findNode = (root, path) => {
        if (!root) return null;
        if (!path) return root;
        const segments = path.split('/').filter(Boolean);
        let node = root;
        for (const seg of segments) {
            node = node?.folders?.find(f => f.name === seg);
            if (!node) return null;
        }
        return node;
    };

    // Inject any client-side pending folders into the live tree so they render
    // alongside real folders. Pending entries that the API now returns (because
    // an icon was uploaded into them) are filtered out so we don't double-show.
    const mergePendingFolders = (root, pending) => {
        if (!root || pending.length === 0) return root;
        const clone = JSON.parse(JSON.stringify(root));
        pending.forEach(({ path, name }) => {
            const parent = findNode(clone, path);
            if (parent && !parent.folders.some(f => f.name === name)) {
                parent.folders.push({
                    name,
                    path: path ? `${path}/${name}` : name,
                    folders: [],
                    images: []
                });
            }
        });
        return clone;
    };

    const getIcons = function () {
        if (!stringUtilities.isNullOrEmpty(organisationId)) {
            setIsLoading(true);
            api.getIconsForOrganisation(organisationId, (data) => {
                setIcons(annotatePaths(data));
                setIsLoading(false);
            }, () => {
                setIsLoading(false);
            });
        }
    };

    useEffect(() => {
        if (!organisationId) return;
        setCurrentPath("");
        try {
            const stored = sessionStorage.getItem(pendingFoldersKey(organisationId));
            setPendingFolders(stored ? JSON.parse(stored) : []);
        } catch {
            setPendingFolders([]);
        }
        getIcons();
    }, [organisationId]);

    // Persist pending folders to session storage so a refresh keeps them around.
    useEffect(() => {
        if (!organisationId) return;
        try {
            sessionStorage.setItem(pendingFoldersKey(organisationId), JSON.stringify(pendingFolders));
        } catch {
            /* storage full or unavailable — ignore */
        }
    }, [pendingFolders, organisationId]);

    // After each fetch, drop pending folders that now exist in the real tree.
    useEffect(() => {
        if (!icons) return;
        setPendingFolders(prev => prev.filter(({ path, name }) => {
            const parent = findNode(icons, path);
            return !parent?.folders?.some(f => f.name === name);
        }));
    }, [icons]);

    const mergedIcons = mergePendingFolders(icons, pendingFolders);
    const currentNode = findNode(mergedIcons, currentPath);
    const currentParentName = currentNode?.name ?? "";
    const breadcrumbSegments = currentPath ? currentPath.split('/').filter(Boolean) : [];

    const navigateTo = (path) => setCurrentPath(path);

    const addFolder = () => setShowFolderModal(true);
    const cancelFolderModal = () => setShowFolderModal(false);

    const saveFolder = function (name) {
        const cleanName = sanitizeFolderName(name);
        if (!cleanName) return;
        setPendingFolders(prev =>
            prev.some(p => p.path === currentPath && p.name === cleanName)
                ? prev
                : [...prev, { path: currentPath, name: cleanName }]
        );
        cancelFolderModal();
    };

    const uploadFiles = (files) => {
        const svgs = files.filter(f => f.name.toLowerCase().endsWith('.svg'));
        if (svgs.length === 0) return;
        setIsLoading(true);
        let pending = svgs.length;
        const done = () => {
            pending--;
            if (pending === 0) getIcons();
        };
        svgs.forEach(file => {
            api.addIcon(organisationId, currentPath, file.name, file, done, done);
        });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        uploadFiles(Array.from(e.dataTransfer.files));
    };

    const handleDropZoneClick = () => fileInputRef.current?.click();

    const handleFileInput = (e) => {
        uploadFiles(Array.from(e.target.files || []));
        e.target.value = "";
    };

    return <>
        <Container className='pt-5 pb-lg-4 mt-5 mb-sm-2'>
            <Row>
                <Col className='mb-5'>
                    <div className='d-flex justify-content-between my-5'>
                        <h1 className="h2 m-0">Icons</h1>

                        <div className='d-flex justify-content-end'>
                            <OrganisationSelector value={organisationId} organisations={props.organisationalUnits} onChange={setOrganisationId} />
                            {!isLoading &&
                                <Button size='sm' className='ms-2' onClick={addFolder}>
                                    <i className='fi-folder-plus me-2'></i>Add Folder
                                </Button>
                            }
                        </div>
                    </div>

                    {isLoading && <Loading />}

                    {!isLoading && currentNode && <>
                        <Breadcrumb>
                            <Breadcrumb.Item
                                active={!currentPath}
                                href="#"
                                onClick={(e) => { e.preventDefault(); navigateTo(""); }}>
                                {icons?.name ?? "Root"}
                            </Breadcrumb.Item>
                            {breadcrumbSegments.map((seg, idx) => {
                                const segPath = breadcrumbSegments.slice(0, idx + 1).join('/');
                                const isActive = idx === breadcrumbSegments.length - 1;
                                return (
                                    <Breadcrumb.Item
                                        key={segPath}
                                        active={isActive}
                                        href="#"
                                        onClick={(e) => { e.preventDefault(); if (!isActive) navigateTo(segPath); }}>
                                        {seg}
                                    </Breadcrumb.Item>
                                );
                            })}
                        </Breadcrumb>

                        <div
                            className='rounded p-5 mb-4 text-center'
                            style={{
                                cursor: 'pointer',
                                border: `2px dashed ${isDragging ? '#0d6efd' : '#ced4da'}`,
                                backgroundColor: isDragging ? '#e7f1ff' : '#f8f9fa',
                                transition: 'background-color 0.15s, border-color 0.15s'
                            }}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={handleDropZoneClick}>
                            <p className='mb-2'>Drag and drop your SVG file(s) into the area</p>
                            <p className='mb-0 text-muted'>- or click here to choose files</p>
                            <input
                                ref={fileInputRef}
                                type='file'
                                accept='.svg'
                                multiple
                                style={{ display: 'none' }}
                                onChange={handleFileInput}
                            />
                        </div>

                        <Row className='g-3'>
                            {currentNode.folders?.map(folder => (
                                <Col xs={6} sm={4} md={3} lg={2} key={`folder-${folder.path}`}>
                                    <div className='border rounded p-2 text-center position-relative d-flex flex-column align-items-center justify-content-center' style={{ cursor: 'pointer', minHeight: 140 }} onClick={() => navigateTo(folder.path)}>
                                        <div className="position-absolute start-0 top-0 pt-2 ps-2">
                                            <span className="d-table badge bg-primary mb-1">{stringUtilities.formatDecimal(countImagesRecursive(folder), 0)}</span>
                                        </div>
                                        <i className='fi-folder my-auto' style={{ fontSize: '3rem' }}></i>
                                        <p className='mb-0 text-truncate fs-xs w-100'>{folder.name}</p>
                                    </div>
                                </Col>
                            ))}
                            {currentNode.images?.map(img => (
                                <Col xs={6} sm={4} md={3} lg={2} key={`img-${img.url}`}>
                                    <a
                                        href={pointUtilities.getBaseSlugForIcons() + img.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className='border rounded p-2 text-center position-relative d-flex flex-column align-items-center justify-content-center text-decoration-none text-reset'
                                        style={{ cursor: 'pointer', minHeight: 140 }}>
                                        <img
                                            className="my-auto"
                                            src={pointUtilities.getBaseSlugForIcons() + img.url}
                                            loading='lazy'
                                            style={{ maxHeight: 64, maxWidth: '100%' }}
                                            alt={img.name}
                                        />
                                        <p className='mb-0 text-truncate fs-xs w-100'>{img.name}{img.url.substring(img.url.lastIndexOf('.'))}</p>
                                    </a>
                                </Col>
                            ))}
                            {currentNode.folders?.length === 0 && currentNode.images?.length === 0 && (
                                <Col>
                                    <p className='text-muted'>This folder is empty.</p>
                                </Col>
                            )}
                        </Row>
                    </>}
                </Col>
            </Row>
        </Container>

        {showFolderModal &&
            <FolderModal parentName={currentParentName} onCancel={cancelFolderModal} onSave={saveFolder} />
        }
    </>;
}
